import React, { useMemo } from 'react';
import { Typography, useStateInRef } from 'seagull';
import { BillingTriggerType } from "../../../../types/api.model";
import styles from "./BillingControl.module.scss";
import { applyChangesToService, currencyFormatter, getPauseDisabledTooltip, stringifyServiceCost } from "./BillingControl.utils";
import BillingControlSection from "./ServicesSection";
import noop from 'lodash/noop';
import AutomaticServiceBillingItem from "./LineItems/AutomaticServiceBillingItem";
import ManualServiceBillingItem from "./LineItems/ManualServiceBillingItem";
import DeliveryServiceBillingItem from "./LineItems/DeliveryServiceBillingItem";
import CreditBillingItem from "./LineItems/CreditBillingItem";
import { RecurrencePeriodTypes } from "../../../../models/service/model";
import { isServiceRelevantForBillingHub } from "../BillingHub.utils";
import { BusinessChannelEventBinder, PusherEvent } from "../../../../pusher";
export default function BillingControl(props) {
  const {
    agreement,
    deliverablesBillCommandsMap,
    invoiceDateInfo,
    billingControlData,
    onRefresh
  } = props;
  const getProps = useStateInRef(props);
  const onBindToUserChannel = props.onBindToUserChannel || noop;
  const {
    services
  } = agreement;
  const {
    lineItemsPerSection,
    hasItems
  } = useMemo(() => {
    const {
      onRefresh
    } = getProps();
    const lineItemsPerSection = {
      manual: {
        oneTime: [],
        [RecurrencePeriodTypes.Weekly]: [],
        [RecurrencePeriodTypes.BiWeekly]: [],
        [RecurrencePeriodTypes.Monthly]: [],
        [RecurrencePeriodTypes.Quarterly]: [],
        [RecurrencePeriodTypes.Yearly]: []
      },
      recurring: {
        onApproval: [],
        [RecurrencePeriodTypes.Weekly]: [],
        [RecurrencePeriodTypes.BiWeekly]: [],
        [RecurrencePeriodTypes.Monthly]: [],
        [RecurrencePeriodTypes.Quarterly]: [],
        credit: []
      }
    };
    const servicesToDisplay = services.map(applyChangesToService).filter(s => {
      var _deliverablesBillComm, _deliverablesBillComm2;

      const hasActiveBillCommand = ((_deliverablesBillComm = deliverablesBillCommandsMap[s.id]) === null || _deliverablesBillComm === void 0 ? void 0 : (_deliverablesBillComm2 = _deliverablesBillComm[0]) === null || _deliverablesBillComm2 === void 0 ? void 0 : _deliverablesBillComm2.status) === 'active';
      return isServiceRelevantForBillingHub(s) && !hasActiveBillCommand;
    }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    servicesToDisplay.forEach(service => {
      const {
        name,
        id,
        cost,
        billing
      } = service;
      const {
        costDisplay,
        unitCapDisplay
      } = stringifyServiceCost(cost);

      switch (billing.trigger) {
        case BillingTriggerType.Ongoing:
          {
            const disabled = invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected;
            const idx = lineItemsPerSection.recurring[billing.recurrencePeriod].length;
            lineItemsPerSection.recurring[billing.recurrencePeriod].push( /*#__PURE__*/React.createElement(AutomaticServiceBillingItem, {
              key: "".concat(name, "-").concat(idx),
              idx: idx,
              serviceId: id,
              serviceName: name,
              costDisplay: costDisplay,
              billing: billing,
              agreement: agreement,
              disabled: disabled,
              tooltipTitle: disabled ? getPauseDisabledTooltip(false) : undefined,
              onRefresh: onRefresh
            }));
            break;
          }

        case BillingTriggerType.OnApproval:
          {
            const idx = lineItemsPerSection.recurring.onApproval.length;
            lineItemsPerSection.recurring.onApproval.push( /*#__PURE__*/React.createElement(AutomaticServiceBillingItem, {
              key: "".concat(name, "-").concat(idx),
              disabled: true,
              idx: idx,
              serviceId: id,
              serviceName: name,
              costDisplay: costDisplay,
              billing: billing,
              agreement: agreement,
              tooltipTitle: "This one-time service will be billed on the agreement's effective date",
              onRefresh: onRefresh
            }));
            break;
          }

        case BillingTriggerType.OneTimeManual:
        case BillingTriggerType.RepeatableManual:
          {
            const category = billing.trigger === BillingTriggerType.RepeatableManual ? lineItemsPerSection.manual[billing.recurrencePeriod] : lineItemsPerSection.manual.oneTime;
            const idx = category.length;
            const manualServiceBilling = billingControlData.manualServices[service.id];
            category.push( /*#__PURE__*/React.createElement(ManualServiceBillingItem, {
              key: "".concat(name, "-").concat(idx),
              idx: idx,
              name: name,
              costDisplay: costDisplay,
              unitCapDisplay: unitCapDisplay,
              manualServiceBilling: manualServiceBilling,
              service: service,
              agreement: agreement,
              invoiceDateInfo: invoiceDateInfo,
              totalCredits: billingControlData.totalCredits,
              onRefresh: onRefresh
            }));
            break;
          }

        case BillingTriggerType.OneTimeDelivery:
          {
            const idx = lineItemsPerSection.manual.oneTime.length;
            lineItemsPerSection.manual.oneTime.push( /*#__PURE__*/React.createElement(DeliveryServiceBillingItem, {
              key: "".concat(name, "-").concat(idx),
              idx: idx,
              name: name,
              costDisplay: costDisplay,
              unitCapDisplay: unitCapDisplay,
              service: service,
              agreement: agreement,
              billCommands: deliverablesBillCommandsMap[service.id],
              onRefresh: onRefresh
            }));
            break;
          }
      }
    });
    billingControlData.credits.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())).forEach(credit => {
      const index = lineItemsPerSection.recurring.credit.length;
      const disabled = invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected;
      lineItemsPerSection.recurring.credit.push( /*#__PURE__*/React.createElement(CreditBillingItem, {
        key: credit.id,
        index: index,
        id: credit.id,
        name: credit.name,
        isPaused: credit.isPaused,
        disabled: disabled,
        tooltipTitle: disabled ? getPauseDisabledTooltip(true) : undefined,
        costDisplay: currencyFormatter(credit.totalAmount.toDP(2).abs()),
        agreement: agreement,
        onRefresh: onRefresh
      }));
    });
    return {
      lineItemsPerSection,
      hasItems: !!servicesToDisplay.length || !!billingControlData.credits.length
    };
  }, [services, deliverablesBillCommandsMap, agreement, invoiceDateInfo, billingControlData]);

  function onPusherInvoiceEvent(event) {
    const {
      RelationshipId,
      ServiceIds
    } = event.payload.invoiceIssued; // Why do we do this?
    // After clicking on "bill now" for a manual service, its isBillable state changes to false until the invoice is issued asynchronously.
    // The bill button is disabled in this state.
    // We listen to a pusher event to know when we should refresh the data to see if the service has "isBillable: true" again.

    const shouldRefresh = RelationshipId === agreement.id && agreement.services.some(s => !s.isBillable && ServiceIds.includes(s.id));

    if (shouldRefresh) {
      onRefresh({
        silent: true
      });
    }
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(BusinessChannelEventBinder, {
    eventName: PusherEvent.InvoiceEvent,
    onBind: onBindToUserChannel,
    callback: onPusherInvoiceEvent
  }), hasItems ? /*#__PURE__*/React.createElement("ul", {
    "data-testid": "billing-control"
  }, /*#__PURE__*/React.createElement("li", {
    hidden: Object.values(lineItemsPerSection.manual).every(i => i.length === 0),
    "data-testid": "manual-services-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.trigger
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.triggerText,
    weight: "bold"
  }, "Manual services")), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "One time",
    hidden: !lineItemsPerSection.manual.oneTime.length,
    "data-testid": "manual-oneTime-section"
  }, lineItemsPerSection.manual.oneTime), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Weekly",
    hidden: !lineItemsPerSection.manual.weekly.length,
    "data-testid": "manual-weekly-section"
  }, lineItemsPerSection.manual.weekly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Every Two Weeks",
    hidden: !lineItemsPerSection.manual.biweekly.length,
    "data-testid": "manual-biweekly-section"
  }, lineItemsPerSection.manual.biweekly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Monthly",
    hidden: !lineItemsPerSection.manual.monthly.length,
    "data-testid": "manual-monthly-section"
  }, lineItemsPerSection.manual.monthly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Quarterly",
    hidden: !lineItemsPerSection.manual.quarterly.length,
    "data-testid": "manual-quarterly-section"
  }, lineItemsPerSection.manual.quarterly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Yearly",
    hidden: !lineItemsPerSection.manual.yearly.length,
    "data-testid": "manual-yearly-section"
  }, lineItemsPerSection.manual.yearly)), /*#__PURE__*/React.createElement("li", {
    hidden: Object.values(lineItemsPerSection.recurring).every(i => i.length === 0),
    "data-testid": "recurring-services-group"
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.trigger
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.triggerText,
    weight: "bold"
  }, "Automatic services")), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "One time, on approval",
    hidden: !lineItemsPerSection.recurring.onApproval.length,
    "data-testid": "recurring-onApproval-section"
  }, lineItemsPerSection.recurring.onApproval), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Weekly",
    hidden: !lineItemsPerSection.recurring.weekly.length,
    "data-testid": "recurring-weekly-section"
  }, lineItemsPerSection.recurring.weekly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "very Two Weeks",
    hidden: !lineItemsPerSection.recurring.biweekly.length,
    "data-testid": "recurring-biweekly-section"
  }, lineItemsPerSection.recurring.biweekly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Monthly",
    hidden: !lineItemsPerSection.recurring.monthly.length,
    "data-testid": "recurring-monthly-section"
  }, lineItemsPerSection.recurring.monthly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Quarterly",
    hidden: !lineItemsPerSection.recurring.quarterly.length,
    "data-testid": "recurring-quarterly-section"
  }, lineItemsPerSection.recurring.quarterly), /*#__PURE__*/React.createElement(BillingControlSection, {
    title: "Credit",
    hidden: !lineItemsPerSection.recurring.credit.length,
    "data-testid": "recurring-credit-section"
  }, lineItemsPerSection.recurring.credit))) : /*#__PURE__*/React.createElement("div", {
    "data-testid": "billing-control-empty-state",
    className: styles.emptyState
  }, /*#__PURE__*/React.createElement(Typography, null, "There are no billable services to show")));
}